import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { fetchSettings } from "@/lib/api";
import { CONTACT_INFO } from "@/i18n/content";

const SiteContentContext = createContext(null);

export const SiteContentProvider = ({ children }) => {
  const [settings, setSettings] = useState({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(() => {
    return fetchSettings()
      .then((r) => setSettings(r.data || {}))
      .catch(() => setSettings({}))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Apply brand colors + favicon overrides at runtime.
  useEffect(() => {
    const root = document.documentElement;
    if (settings.brand_primary_color) {
      root.style.setProperty("--primary", settings.brand_primary_color);
    } else {
      root.style.removeProperty("--primary");
    }
    if (settings.brand_bg_color) {
      root.style.setProperty("--bg", settings.brand_bg_color);
    } else {
      root.style.removeProperty("--bg");
    }
    if (settings.brand_favicon_url) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      const abs = settings.brand_favicon_url.startsWith("/api/")
        ? `${process.env.REACT_APP_BACKEND_URL || ""}${settings.brand_favicon_url}`
        : settings.brand_favicon_url;
      link.href = abs;
    }
  }, [settings.brand_primary_color, settings.brand_bg_color, settings.brand_favicon_url]);

  // Inject Google Analytics 4 when a Measurement ID is configured.
  useEffect(() => {
    const gaId = (settings.ga_measurement_id || "").trim();
    if (!gaId) return;
    if (window.__gaLoadedId === gaId) return;
    // remove previously injected scripts (in case ID changed)
    document.querySelectorAll("script[data-ga-injected='1']").forEach((s) => s.remove());
    const s1 = document.createElement("script");
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    s1.setAttribute("data-ga-injected", "1");
    document.head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.setAttribute("data-ga-injected", "1");
    s2.text = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${gaId}');`;
    document.head.appendChild(s2);
    window.__gaLoadedId = gaId;
  }, [settings.ga_measurement_id]);

  // Merge overrides with defaults from content.js
  const contact = useMemo(() => ({
    phone: settings.contact_phone || CONTACT_INFO.phone,
    whatsapp: settings.contact_whatsapp || CONTACT_INFO.whatsapp,
    email: settings.contact_email || CONTACT_INFO.email,
    website: settings.contact_website || CONTACT_INFO.website,
    address: settings.address_ar || CONTACT_INFO.address,
    addressEn: settings.address_en || CONTACT_INFO.addressEn,
    addressKu: settings.address_ku || CONTACT_INFO.addressKu,
    social: {
      facebook: settings.social_facebook || CONTACT_INFO.social.facebook,
      instagram: settings.social_instagram || CONTACT_INFO.social.instagram,
      linkedin: settings.social_linkedin || CONTACT_INFO.social.linkedin,
      tiktok: settings.social_tiktok || CONTACT_INFO.social.tiktok,
    },
  }), [settings]);

  const identity = useMemo(() => ({
    name_ar: settings.identity_name_ar || "مدينتي",
    name_en: settings.identity_name_en || "Madenati",
    name_ku: settings.identity_name_ku || "مەدینەتی",
    tagline_ar: settings.identity_tagline_ar || "حلول ذكية لمستقبل أفضل",
    tagline_en: settings.identity_tagline_en || "Smart solutions for a better future",
    tagline_ku: settings.identity_tagline_ku || "چارەسەری زیرەک بۆ داهاتوویەکی باشتر",
  }), [settings]);

  const value = useMemo(() => ({ settings, loaded, refresh, contact, identity }), [settings, loaded, refresh, contact, identity]);
  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
};

export const useSite = () => {
  const ctx = useContext(SiteContentContext);
  if (!ctx) return { settings: {}, loaded: false, refresh: () => {}, contact: CONTACT_INFO, identity: {} };
  return ctx;
};

export const useContactInfo = () => useSite().contact;

// Return the override value for `key` (per-lang suffix optional), fallback to `fallback`.
export const useOverride = (key, fallback = "") => {
  const { settings } = useSite();
  const v = settings?.[key];
  return v !== undefined && v !== null && v !== "" ? v : fallback;
};
